import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MapPin, ArrowDown } from "lucide-react";
import { SITE, IMAGES, waMenuLink, scrollToId } from "@/data/site";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.85, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center pt-[124px] md:pt-[136px] lg:pt-[172px] overflow-hidden">
      <span aria-hidden="true" className="absolute -bottom-8 -right-4 font-serif italic text-[24vw] leading-none text-terracotta/[0.06] pointer-events-none select-none">
        saporé
      </span>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center py-16 lg:py-0">
        <div className="lg:col-span-7 relative z-10">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-xs md:text-sm uppercase tracking-[0.3em] text-terracotta font-medium"
          >
            SAPORÉ — Gastronomia calda d'asporto · Lecce
          </motion.p>

          <h1 className="mt-6 font-serif font-medium text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[0.95] tracking-tighter text-charred">
            <Line delay={0.25}>La cucina di casa,</Line>
            <Line delay={0.37}><span className="italic text-terracotta">pronta da portare</span></Line>
            <Line delay={0.49}>con te.</Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7 }}
            className="mt-8 max-w-xl text-base md:text-lg text-charred/70 leading-relaxed"
          >
            Primi, secondi, contorni, focacce e specialità salentine preparate ogni giorno.
            Per la pausa pranzo, una cena in famiglia o un'occasione speciale: da SAPORÉ trovi piatti caldi da portare via e gustare dove preferisci.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToId("menu")}
              data-testid="hero-menu-btn"
              className="rounded-full bg-terracotta text-cream px-7 py-3.5 text-sm font-semibold hover:bg-terracotta-dark hover:-translate-y-0.5 transition-all duration-300"
            >
              Scopri il menù
            </button>
            <a
              href={waMenuLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-order-btn"
              className="rounded-full border border-charred/30 text-charred px-7 py-3.5 text-sm font-semibold hover:bg-charred hover:text-cream hover:-translate-y-0.5 transition-all duration-300"
            >
              Ordina per il ritiro
            </a>
            <div className="flex items-center gap-5 text-sm font-medium">
              <a href={SITE.phoneHref} data-testid="hero-call-link" className="inline-flex items-center gap-2 text-charred/70 hover:text-terracotta transition-colors">
                <Phone size={15} /> Chiamaci
              </a>
              <a href={SITE.mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="hero-directions-link" className="inline-flex items-center gap-2 text-charred/70 hover:text-terracotta transition-colors">
                <MapPin size={15} /> Indicazioni stradali
              </a>
            </div>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -top-4 -left-4 w-full h-full border border-terracotta/40 rounded-[4px]" aria-hidden="true" />
            <div className="noise-frame relative overflow-hidden rounded-[4px] aspect-[4/5]">
              <motion.img
                src={IMAGES.hero}
                alt="Ingredienti freschi della cucina salentina"
                style={{ y: imgY }}
                className="w-full h-[115%] object-cover"
              />
            </div>
            <div className="absolute -bottom-5 -right-3 md:-right-6 bg-charred text-cream px-5 py-4 rounded-[4px] shadow-xl">
              <p className="font-serif italic text-lg leading-tight">Il tuo pranzo<br />è già pronto.</p>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.button
        onClick={() => scrollToId("chi-siamo")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden lg:grid place-items-center w-10 h-10 rounded-full border border-charred/20 text-charred/50 hover:text-terracotta hover:border-terracotta transition-colors"
        aria-label="Scorri"
        data-testid="hero-scroll-indicator"
      >
        <ArrowDown size={16} className="animate-bounce" />
      </motion.button>
    </section>
  );
}
